import { Component, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AppConfigService } from '../../services/app-config.service';
import { RevealDirective } from '../../directives/reveal.directive';
import { PoliticaPrivacidadComponent } from '../politica-privacidad/politica-privacidad.component';

interface Motivo {
  id: 'comercial' | 'soporte' | 'medida';
  label: string;
  placeholder: string;
}

const MOTIVOS: Motivo[] = [
  {
    id: 'comercial',
    label: 'Comercial',
    placeholder: 'Cuéntanos qué hace tu empresa y qué necesitas resolver.',
  },
  {
    id: 'soporte',
    label: 'Soporte',
    placeholder:
      'Cuéntanos qué ha pasado y en qué pantalla. Si puedes, indica tu subdominio.',
  },
  {
    id: 'medida',
    label: 'A medida',
    placeholder:
      'Describe lo que el estándar no cubre; vemos si encaja sobre la plataforma.',
  },
];

@Component({
  selector: 'app-contacto',
  standalone: true,
  imports: [
    ButtonModule,
    ReactiveFormsModule,
    RouterLink,
    ToastModule,
    DialogModule,
    RevealDirective,
    PoliticaPrivacidadComponent,
  ],
  providers: [MessageService],
  templateUrl: './contacto.component.html',
  styleUrl: './contacto.component.scss',
})
export class ContactoComponent {
  appConfig = inject(AppConfigService);
  private http = inject(HttpClient);
  readonly #messageService = inject(MessageService);

  readonly motivos = MOTIVOS;
  motivo = signal<Motivo['id']>('comercial');
  mensajePlaceholder = computed(
    () =>
      (this.motivos.find(m => m.id === this.motivo()) ?? this.motivos[0])
        .placeholder
  );

  form: FormGroup;
  showPrivacyModal = false;
  enviando = false;
  private submitted = false;

  constructor(readonly fb: FormBuilder) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      phone: [''],
      email: ['', [Validators.required, Validators.email]],
      message: ['', Validators.required],
      privacyAccepted: [false, Validators.requiredTrue],
    });
  }

  pickMotivo(id: Motivo['id']): void {
    this.motivo.set(id);
  }

  invalid(name: string): boolean {
    const c = this.form.get(name);
    return !!c && c.invalid && (c.touched || this.submitted);
  }

  enviar(): void {
    this.submitted = true;
    this.form.markAllAsTouched();
    if (this.form.invalid || this.enviando) return;

    this.enviando = true;
    const raw = this.form.getRawValue();
    const motivo =
      this.motivos.find(m => m.id === this.motivo()) ?? this.motivos[0];
    const payload = {
      name: raw.name,
      phone: raw.phone,
      email: raw.email,
      message: raw.message,
      asunto: motivo.label,
    };

    this.http
      .post<{ type: string; msg: string }>('/api/contacto/send', payload)
      .subscribe({
        next: () => {
          this.#messageService.add({
            severity: 'success',
            life: 5000,
            closable: true,
            summary: 'Mensaje enviado',
            detail: 'Te responderemos lo antes posible.',
          });
          this.form.reset({
            name: '',
            phone: '',
            email: '',
            message: '',
            privacyAccepted: false,
          });
          this.submitted = false;
          this.enviando = false;
        },
        error: err => {
          const msg =
            err?.error?.msg ||
            'Ha ocurrido un error. Por favor, inténtalo de nuevo.';
          this.#messageService.add({
            severity: 'error',
            sticky: true,
            closable: true,
            summary: 'Error al enviar',
            detail: msg,
          });
          this.enviando = false;
        },
      });
  }
}
